import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiCheckCircle, FiFileText, FiAward, FiUserCheck } from 'react-icons/fi';

const StepsContainer = styled.section`
  background: white;
  padding: 3rem 0;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding: 2.5rem 0;
    min-height: 90vh;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    padding: 2.75rem 0;
    min-height: 95vh;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 3rem 1.5rem;
    min-height: 90vh;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 2.5rem 1rem;
    min-height: 80vh;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    min-height: unset;
    align-items: flex-start;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 1.5rem 0.5rem;
  }
`;

const StepsContent = styled.div`
  max-width: 1275px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 1.5rem;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 0 1.5rem;
    gap: 2rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 0 1rem;
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const StepsLeft = styled.div`
  flex: 1 1 40%;
  min-width: 320px;
  max-width: 500px;
  padding-right: 1rem;

  @media (max-width: 1100px) {
    max-width: 100%;
    padding-right: 0;
  }
`;

const Badge = styled.div`
  background: white;
  color: ${props => props.theme.colors.secondary};
  font-weight: 700;
  font-size: 1.05rem;
  border-radius: 999px;
  box-shadow: 0 2px 12px rgba(143, 226, 0, 0.1), 0 1.5px 6px rgba(0, 0, 0, 0.04);
  padding: 10px 32px;
  border: 2px solid #e6f3df;
  letter-spacing: 0.04em;
  display: inline-block;
  margin-bottom: 1.5rem;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 10px 28px;
    font-size: 1.02rem;
    margin-bottom: 1.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 9px 24px;
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 768px) {
    padding: 8px 18px;
    font-size: 0.98rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.secondary};
  font-size: 2.7rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  line-height: 1.1;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 2.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 2.3rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  color: #444;
  font-size: 1.08rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const SectionDescription = styled.p`
  color: #222;
  font-size: 1.08rem;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const CTABox = styled.div`
  background: #f7f7f7;
  border-radius: 28px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 2.5rem 2rem 2rem 2rem;
  margin-top: 2.5rem;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 2rem 1.5rem;
    margin-top: 2rem;
    max-width: 100%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 1.75rem 1.25rem;
    margin-top: 1.75rem;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-top: 1.5rem;
    max-width: 100%;
  }
`;

const CTATitle = styled.h3`
  color: ${props => props.theme.colors.secondary};
  font-size: 2.1rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.1;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 2rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 1.9rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const CTADescription = styled.p`
  color: #222;
  font-size: 1.05rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const CTADescription2 = styled.p`
  color: #444;
  font-size: 1.01rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const CTAButton = styled(Link)`
  background: ${props => props.theme.colors.secondary};
  color: white;
  font-size: 1.15rem;
  font-weight: 700;
  border: none;
  border-radius: 24px;
  padding: 14px 38px;
  box-shadow: 0 2px 8px rgba(143, 226, 0, 0.1);
  text-decoration: none;
  transition: all 0.2s ease;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(143, 226, 0, 0.2);
  }
`;

const StepsRight = styled.div`
  flex: 1 1 60%;
  min-width: 350px;
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;

  @media (max-width: 1100px) {
    min-width: 0;
    width: 100%;
  }
`;

const StepperContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2.25rem;
  width: 100%;
  max-width: 500px;
  justify-content: center;
  align-items: center;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    gap: 2rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    gap: 1.75rem;
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const StepperLine = styled.div`
  position: absolute;
  left: 44px;
  top: 0;
  width: 4px;
  height: 100%;
  background: #e6f3df;
  z-index: 0;
  border-radius: 2px;
`;

const StepperLineActive = styled.div`
  position: absolute;
  left: 44px;
  top: 0;
  width: 4px;
  height: ${props => props.height}px;
  background: linear-gradient(to bottom, ${props => props.theme.colors.secondary} 0%, #e6f3df 100%);
  z-index: 1;
  border-radius: 2px;
  transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(${props => props.offsetY}px);
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  position: relative;
  opacity: ${props => props.isActive ? 1 : 0.3};
  transform: translateY(${props => props.isActive ? 0 : 40}px);
  transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  width: 100%;
  max-width: 500px;

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    gap: 1.25rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const StepNumber = styled.div`
  min-width: 44px;
  min-height: 44px;
  max-width: 44px;
  max-height: 44px;
  background: ${props => props.theme.colors.secondary};
  border: 3px solid white;
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(143, 226, 0, 0.18);
  z-index: 3;
  position: relative;
  flex-shrink: 0;
`;

const StepCard = styled.div`
  background: linear-gradient(135deg, #fafdff 60%, #e6f3df 100%);
  border: 2.5px solid #e6f3df;
  border-radius: 22px;
  box-shadow: 0 8px 32px rgba(143, 226, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.07);
  padding: 2rem;
  min-width: 320px;
  max-width: 420px;
  transition: all 0.2s ease;
  opacity: ${props => props.isActive ? 1 : 0.7};
  filter: ${props => props.isActive ? 'none' : 'blur(2px)'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 22px;
    pointer-events: none;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.04) 100%);
    z-index: 1;
  }

  &:hover {
    box-shadow: 0 12px 36px rgba(143, 226, 0, 0.22), 0 4px 12px rgba(0, 0, 0, 0.13);
    border-color: ${props => props.theme.colors.secondary};
    transform: translateY(-6px) scale(1.03);
    z-index: 3;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    min-width: 280px;
    max-width: 380px;
    padding: 1.5rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    min-width: 260px;
    max-width: 360px;
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    min-width: 0;
    max-width: 100%;
    padding: 1rem;
  }
`;

const StepCardTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1.18rem;
  font-weight: 700;
  color: #222;
  z-index: 2;
  position: relative;
`;

const StepCardContent = styled.p`
  margin: 0;
  font-size: 1.01rem;
  color: #222;
  line-height: 1.5;
  z-index: 2;
  position: relative;

  b {
    font-weight: 600;
    color: #111;
  }
`;

const ReadyText = styled.div`
  margin-top: 2rem;
  font-size: 1.18rem;
  color: #222;
  font-weight: 600;
  text-align: left;
  line-height: 1.4;
  max-width: 480px;

  span {
    color: ${props => props.theme.colors.secondary};
    font-weight: 700;
  }

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    margin-top: 2rem;
    font-size: 1.15rem;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    margin-top: 1.75rem;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    font-size: 1.05rem;
  }
`;

const StepsSection = () => {
  const [activeSteps, setActiveSteps] = useState([0]);
  const [lineHeight, setLineHeight] = useState(0);
  const [lineOffsetY, setLineOffsetY] = useState(0);
  const stepRefs = useRef([]);
  const containerRef = useRef(null);

  const steps = [
    {
      number: 1,
      title: 'Free Skills Assessment',
      content: (
        <>
          <b>Step 1 — Speak to RPL Support</b><br />
          Talk to us about your goals and our friendly team will guide you toward the qualification that matches your experience and future plans.
        </>
      ),
      icon: FiUserCheck
    },
    {
      number: 2,
      title: 'Portfolio of Experience',
      content: (
        <>
          <b>Step 2 — Show What You Know</b><br />
          Your dedicated RPL consultant will help you build a clear portfolio of evidence. This can include previous qualifications, transcripts, certificates, work samples, photos or videos, letters of reference, and your current resume.
        </>
      ),
      icon: FiFileText
    },
    {
      number: 3,
      title: 'Evidence Review',
      content: (
        <>
          <b>Step 3 — Expert Assessment</b><br />
          Your portfolio is assessed by one of our trusted partner Registered Training Organisations (RTOs). If any extra evidence is needed, your assessor will reach out. If there are skill gaps, the RTO may arrange free upskilling to help you meet all requirements.
        </>
      ),
      icon: FiCheckCircle
    },
    {
      number: 4,
      title: 'You\'re Qualified!',
      content: (
        <>
          <b>Step 4 — Get Recognised</b><br />
          RPL Support works with reputable RTO partners to award qualifications that are nationally recognised in Australia — exactly the same as studying full time. Your qualification can help with licence applications, better job opportunities, and career growth.
        </>
      ),
      icon: FiAward
    }
  ];

  const updateStepperLine = () => {
    if (!containerRef.current || stepRefs.current.length === 0) return;

    const lastActiveIdx = Math.max(...activeSteps);
    const firstStepNumber = stepRefs.current[0]?.querySelector('[data-step-number]');
    const lastStepNumber = stepRefs.current[lastActiveIdx]?.querySelector('[data-step-number]');

    if (firstStepNumber && lastStepNumber) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const firstRect = firstStepNumber.getBoundingClientRect();
      const lastRect = lastStepNumber.getBoundingClientRect();

      const startY = firstRect.top + firstRect.height / 2 - containerRect.top;
      const endY = lastRect.top + lastRect.height / 2 - containerRect.top;
      const height = endY - startY;

      setLineOffsetY(startY);
      setLineHeight(height > 0 ? height : 0);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepIndex = stepRefs.current.indexOf(entry.target);
            if (stepIndex > 0) {
              setActiveSteps(prev => {
                const newActiveSteps = [...prev];
                for (let i = 1; i <= stepIndex; i++) {
                  if (!newActiveSteps.includes(i)) {
                    newActiveSteps.push(i);
                  }
                }
                return newActiveSteps;
              });
            }
          }
        });
      },
      { threshold: 0.4 }
    );

    stepRefs.current.forEach((step, index) => {
      if (index > 0 && step) {
        observer.observe(step);
      }
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    updateStepperLine();
    window.addEventListener('resize', updateStepperLine);
    window.addEventListener('scroll', updateStepperLine);

    return () => {
      window.removeEventListener('resize', updateStepperLine);
      window.removeEventListener('scroll', updateStepperLine);
    };
  }, [activeSteps, updateStepperLine]);

  return (
    <StepsContainer>
      <StepsContent>
        <StepsLeft>
          <Badge>About RPL Support</Badge>
          <SectionTitle>4 Simple Steps to Success</SectionTitle>
          <SectionSubtitle>
            Getting the recognition you deserve has never been easier.
          </SectionSubtitle>
          <SectionDescription>
            At RPL Support, we help you convert your skills, knowledge, and real-world experience into a nationally recognised qualification — without putting your career on hold. Through our streamlined Recognition of Prior Learning (RPL) process, you can gain formal certification faster and open doors to better job opportunities and licensing pathways.
          </SectionDescription>

          <CTABox>
            <CTATitle>You're Just 60 Seconds Away from Transforming Your Career</CTATitle>
            <CTADescription>Don't wait — take the first step towards a brighter future today.</CTADescription>
            <CTADescription2>Join thousands of skilled workers who've used our FREE 60-second skills check to start their RPL journey. Discover how your experience can become an official qualification and take your career to the next level.</CTADescription2>
            <CTAButton to="/contact">Let's GO!</CTAButton>
          </CTABox>
          
          <ReadyText>
            Ready to start?<br />
            Take the free skills check today — and <span>unlock the qualifications you deserve.</span>
          </ReadyText>
        </StepsLeft>

        <StepsRight>
          <StepperContainer ref={containerRef}>
            <StepperLine />
            <StepperLineActive height={lineHeight} offsetY={lineOffsetY} />
            
            {steps.map((step, index) => (
              <StepItem
                key={step.number}
                ref={el => stepRefs.current[index] = el}
                isActive={activeSteps.includes(index)}
              >
                <StepNumber data-step-number>
                  {step.number}
                </StepNumber>
                <StepCard isActive={activeSteps.includes(index)}>
                  <StepCardTitle>{step.title}</StepCardTitle>
                  <StepCardContent>{step.content}</StepCardContent>
                </StepCard>
              </StepItem>
            ))}
          </StepperContainer>
        </StepsRight>
      </StepsContent>
    </StepsContainer>
  );
};

export default StepsSection;